import { authTokenStore, getAuthToken } from './auth-token-store';
import type { SSEApi } from '@/libs/sdk/api/sse';
import { create } from 'zustand';

// Optimistic portfolio deltas for just-confirmed trades. The backend position
// pipeline (subgraph indexing -> block-listener -> DB -> SSE) lags the chain
// by ~15s; entries here bridge that gap and are dropped once the backend row
// lands (SSE user:position_changed marks the DB commit, the next refetch
// replaces the overlay) or after a hard expiry. In-memory only on purpose —
// a stale optimistic row must not survive a reload.
export type PositionOwner = { chainId: number; account: string };
export const samePositionOwner = (a: PositionOwner, b: PositionOwner): boolean =>
  a.chainId === b.chainId && a.account.toLowerCase() === b.account.toLowerCase();

export type PendingPosition = {
  owner: PositionOwner;
  marketAddress: string;
  optionAddress: string;
  marketName: string;
  nameI18n?: Record<string, string>;
  marketPicUrl: string;
  marketDescription: string;
  optionName: string;
  optionDescription: string;
  optionPicUrl: string;
  optionDecimal: number;
  baseTokenAddress?: string;
  eventId?: string;
  // Multi-market event context: without these the overlay row renders as a
  // bare market name while real rows promote the event title (DID pattern).
  eventTitle?: string;
  eventTitleI18n?: Record<string, string>;
  eventMarketCount?: number;
  deadline: string;
  // Net share delta in wei from receipt logs: positive = pending buys,
  // negative = pending sells (merge subtracts from the fetched row).
  shares: string;
  // Cost basis of the POSITIVE portion only (drives synthetic rows and
  // betValue bumps); zero whenever the net delta is <= 0.
  amountIn: string;
  createdAt: number;
  confirmedAt?: number;
};

export type PendingPositionInput = Omit<PendingPosition, 'createdAt' | 'confirmedAt'>;

export type PendingSellInput = {
  owner: PositionOwner;
  marketAddress: string;
  optionAddress: string;
  baseTokenAddress?: string;
  // option-token amount sold, wei string (positive)
  shares: string;
};

// Covers SSE being down entirely (no confirm will ever arrive): the overlay
// disappears and the list falls back to plain backend data.
export const PENDING_POSITION_EXPIRY_MS = 60_000;

export const pendingPositionKey = (marketAddress: string, optionAddress: string, owner: PositionOwner) =>
  `${owner.chainId}:${owner.account}:${marketAddress}:${optionAddress}`.toLowerCase();

// An entry stops being overlaid when the backend caught up (confirmed by SSE
// and the list refetched after that) or when it expired unconfirmed.
export const isPendingSettled = (position: PendingPosition, dataUpdatedAt: number, now: number) =>
  now - position.createdAt > PENDING_POSITION_EXPIRY_MS ||
  (position.confirmedAt !== undefined && dataUpdatedAt > position.confirmedAt);

// Cost basis follows the net share delta: growing the positive side adds the
// trade's cost; shrinking it scales the basis down proportionally; a net <= 0
// has no basis (sells against the backend row, not against pending cost).
const netAmountIn = (prevShares: bigint, prevAmountIn: bigint, nextShares: bigint, tradeAmountIn: bigint): string => {
  if (nextShares <= 0n) return '0';
  if (prevShares <= 0n) return tradeAmountIn.toString();
  if (nextShares >= prevShares) return (prevAmountIn + tradeAmountIn).toString();
  return ((prevAmountIn * nextShares) / prevShares).toString();
};

type PendingPositionState = {
  pending: Record<string, PendingPosition>;
  clear: () => void;
  addPending: (position: PendingPositionInput) => void;
  addPendingSell: (sell: PendingSellInput) => void;
  confirmPending: (marketAddress: string, optionAddress: string, owner: PositionOwner) => void;
  prunePending: (dataUpdatedAt: number, now?: number) => void;
};

export const usePendingPositionStore = create<PendingPositionState>()(set => ({
  pending: {},
  clear: () => set({ pending: {} }),
  addPending: position =>
    set(state => {
      const key = pendingPositionKey(position.marketAddress, position.optionAddress, position.owner);
      const prev = state.pending[key];
      // Re-trade while still pending: accumulate the delta and restart the
      // clock; the fresh trade needs a fresh SSE confirm, so confirmedAt drops.
      let next: PendingPosition;
      if (prev) {
        const prevShares = BigInt(prev.shares);
        const nextShares = prevShares + BigInt(position.shares);
        next = {
          ...position,
          shares: nextShares.toString(),
          amountIn: netAmountIn(prevShares, BigInt(prev.amountIn), nextShares, BigInt(position.amountIn)),
          createdAt: Date.now(),
        };
      } else {
        next = { ...position, createdAt: Date.now() };
      }
      return { pending: { ...state.pending, [key]: next } };
    }),
  addPendingSell: sell =>
    set(state => {
      const key = pendingPositionKey(sell.marketAddress, sell.optionAddress, sell.owner);
      const prev = state.pending[key];
      const prevShares = prev ? BigInt(prev.shares) : 0n;
      const nextShares = prevShares - BigInt(sell.shares);
      // A sell-only entry carries no display metadata: a negative delta never
      // synthesizes a row, it only shrinks/hides the matching fetched row.
      const base: PendingPosition = prev ?? {
        owner: sell.owner,
        marketAddress: sell.marketAddress,
        optionAddress: sell.optionAddress,
        marketName: '',
        marketPicUrl: '',
        marketDescription: '',
        optionName: '',
        optionDescription: '',
        optionPicUrl: '',
        optionDecimal: 0,
        baseTokenAddress: sell.baseTokenAddress,
        deadline: '0',
        shares: '0',
        amountIn: '0',
        createdAt: Date.now(),
      };
      const next: PendingPosition = {
        ...base,
        shares: nextShares.toString(),
        amountIn: netAmountIn(prevShares, BigInt(base.amountIn), nextShares, 0n),
        createdAt: Date.now(),
        confirmedAt: undefined,
      };
      return { pending: { ...state.pending, [key]: next } };
    }),
  confirmPending: (marketAddress, optionAddress, owner) =>
    set(state => {
      const key = pendingPositionKey(marketAddress, optionAddress, owner);
      const entry = state.pending[key];
      if (!entry || entry.confirmedAt !== undefined) return state;
      return { pending: { ...state.pending, [key]: { ...entry, confirmedAt: Date.now() } } };
    }),
  prunePending: (dataUpdatedAt, now = Date.now()) =>
    set(state => {
      const kept = Object.entries(state.pending).filter(([, p]) => !isPendingSettled(p, dataUpdatedAt, now));
      if (kept.length === Object.keys(state.pending).length) return state;
      return { pending: Object.fromEntries(kept) };
    }),
}));

type PendingSseBinding = {
  token: string;
  uid: string;
  owner: PositionOwner;
  dispose: () => void;
};
const sseBindings = new WeakMap<SSEApi, PendingSseBinding>();

// Bind confirmation to the trade's authenticated identity. A queued callback
// from an old stream must never acquire the next account's ownership.
export const wirePendingPositionSse = (sse: SSEApi, owner: PositionOwner, uid: string) => {
  const token = getAuthToken();
  const account = authTokenStore.getState().account;
  if (!token || !uid || account !== owner.account.toLowerCase()) return;
  const previous = sseBindings.get(sse);
  if (previous?.token === token && previous.uid === uid && samePositionOwner(previous.owner, owner)) return;
  previous?.dispose();
  let unsubscribe = () => {};
  const binding: PendingSseBinding = {
    token,
    uid,
    owner: { ...owner },
    dispose: () => {
      unsubscribe();
      if (sseBindings.get(sse) === binding) sseBindings.delete(sse);
      void sse.removeEventListener('user:position_changed', confirm).catch(() => {});
    },
  };
  const confirm = (data: { uid: string; market_address: string; option_address: string }) => {
    if (
      sseBindings.get(sse) !== binding ||
      data.uid !== binding.uid ||
      getAuthToken() !== binding.token ||
      authTokenStore.getState().account !== binding.owner.account.toLowerCase()
    )
      return;
    usePendingPositionStore.getState().confirmPending(data.market_address, data.option_address, binding.owner);
  };
  sseBindings.set(sse, binding);
  unsubscribe = authTokenStore.subscribe(state => {
    if (state.token !== binding.token || state.account !== binding.owner.account.toLowerCase()) binding.dispose();
  });
  void sse.addEventListener('user:position_changed', confirm).catch(binding.dispose);
};
